/**
 * Pure, framework-free composition of the booking-widget embed snippet + standalone link
 * (tasks/booking-widget-embed.md). Everything here is a string transform so the generator UI stays
 * trivially unit-testable and — crucially — so the snippet is built ENTIRELY client-side: the design
 * rejects an embed-config table and any backend endpoint (spec Tech decisions §"Snippet generation").
 * The snippet encodes NOTHING beyond the venue slug + display mode (+ an optional language default);
 * config, rules and branding are fetched live from the API at widget load (R1), so a pasted snippet
 * outlives every deploy and never needs re-copying.
 */

/** The two loader display modes (mirrors the widget loader's `data-mode`, `apps/widget` loader-core). */
export const EMBED_MODES = ["floating", "inline"] as const;
export type EmbedMode = (typeof EMBED_MODES)[number];

/**
 * Optional default-language override baked into the snippet as `data-lang` — the loader already reads
 * and forwards it (`apps/widget` loader.ts → `?lang=`). `auto` means "omit the attribute": the widget
 * then resolves the diner's language against the venue default (localization-i18n R1).
 */
export const EMBED_LANGS = ["auto", "fr", "en"] as const;
export type EmbedLang = (typeof EMBED_LANGS)[number];

export interface SnippetOptions {
  /** The widget origin (`https://book.<domain>`). */
  origin: string;
  /** The venue slug (DOMAIN.md §B.6 `Slug` — `[a-z0-9-]`, so it is safe unquoted in an attribute). */
  slug: string;
  mode: EmbedMode;
  lang?: EmbedLang;
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

/**
 * Resolve the widget origin (`book.<domain>`) for the snippet, standalone link and live preview.
 * Priority:
 *  1. an explicit build override (`VITE_WIDGET_ORIGIN`) — used by the deploy when the naming differs;
 *  2. otherwise swap the leading `app.` DNS label of the current origin for `book.`
 *     (`https://app.acme.com` → `https://book.acme.com`) — the locked platform naming (tasks/README);
 *  3. otherwise the current origin unchanged — the dev/localhost fallback (set the override locally to
 *     point the preview at the widget dev server).
 * Pure so it is unit-tested without a DOM.
 */
export function resolveWidgetOrigin(currentOrigin: string, explicit?: string | null): string {
  const override = explicit?.trim();
  if (override) return stripTrailingSlash(override);
  try {
    const url = new URL(currentOrigin);
    if (url.hostname.startsWith("app.")) {
      url.hostname = `book.${url.hostname.slice("app.".length)}`;
      return url.origin;
    }
    return url.origin;
  } catch {
    return stripTrailingSlash(currentOrigin);
  }
}

/** The forever-stable versioned loader URL (spec R1 — `/embed/v1.js` is a permanent contract). */
export function buildLoaderUrl(origin: string): string {
  return `${stripTrailingSlash(origin)}/embed/v1.js`;
}

/**
 * The single short standalone/bio-link URL `https://book.<domain>/v/<slug>` (spec: "one short URL for
 * bio links"). Carries `channel=embed` implicitly at load; an explicit language default rides as
 * `?lang=` so a shared link opens in the chosen language.
 */
export function buildStandaloneUrl(options: {
  origin: string;
  slug: string;
  lang?: EmbedLang;
}): string {
  const base = `${stripTrailingSlash(options.origin)}/v/${encodeURIComponent(options.slug)}`;
  return options.lang && options.lang !== "auto" ? `${base}?lang=${options.lang}` : base;
}

/** A shared link that attributes its bookings: the standalone page carrying a `channel` the widget maps. */
export function buildChannelUrl(options: {
  origin: string;
  slug: string;
  channel: "google" | "qr";
}): string {
  return `${buildStandaloneUrl(options)}?channel=${options.channel}`;
}

/**
 * The widget URL for the LIVE PREVIEW iframe — the standalone page in embed (framed) mode:
 * `https://book.<domain>/v/<slug>?channel=embed&preview=1[&lang=]`. The preview embeds this directly (a
 * normal cross-origin iframe, not the loader), because inside the app's isolated preview context the
 * loader's `ready`/`resize` postMessage handshake can't complete (the framed widget has no usable
 * referrer to post back to), so a loader-driven preview only ever times out to its fallback anchor. A
 * direct frame needs no handshake: the widget renders its booking flow, sized by the fixed-height iframe.
 * `channel=embed` keeps it in framed (full-bleed) mode; `preview=1` puts the widget in test mode so the
 * manager can walk the whole flow and submit WITHOUT creating a real reservation.
 */
export function buildEmbedFrameUrl(options: {
  origin: string;
  slug: string;
  lang?: EmbedLang;
}): string {
  const params = new URLSearchParams({ channel: "embed", preview: "1" });
  if (options.lang && options.lang !== "auto") params.set("lang", options.lang);
  return `${stripTrailingSlash(options.origin)}/v/${encodeURIComponent(options.slug)}?${params.toString()}`;
}

/**
 * Compose the copy-paste `<script>` snippet. `async` so it never blocks the host page; slug + mode
 * (+ optional lang) in `data-*` — and nothing else (the rejected alternative is server-persisted embed
 * config). The venue pastes this ONCE, forever.
 */
export function buildSnippet(options: SnippetOptions): string {
  const attrs = [
    `src="${buildLoaderUrl(options.origin)}"`,
    `data-venue="${options.slug}"`,
    `data-mode="${options.mode}"`,
  ];
  if (options.lang && options.lang !== "auto") attrs.push(`data-lang="${options.lang}"`);
  return `<script async ${attrs.join(" ")}></script>`;
}

/**
 * Minimal, self-contained HTML document that hosts the real snippet — the source for the live-preview
 * iframe's `srcdoc`. Rendering the actual loader inside an isolated browsing context is what proves the
 * pasted snippet works AND scopes a `floating` overlay to the preview box (its fixed positioning is
 * relative to the iframe viewport, never the surrounding SPA).
 */
export function buildPreviewDocument(snippet: string): string {
  return (
    `<!doctype html><html lang="fr"><head><meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width,initial-scale=1">` +
    `<style>html,body{margin:0}body{padding:16px;` +
    `font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#111}</style>` +
    `</head><body>${snippet}</body></html>`
  );
}
